<script>
import axios from 'axios'
import http from '../../../utils/services/http'

/**
 * Modal EXCLUSIVO de cobrança de mensalidades em atraso.
 *
 * Aparece SÓ para o cliente webleiloes.com.br (gate por domínio-base), em tela
 * cheia (100%) para atrapalhar a navegação. NÃO tem "não ver mais" nem cookie —
 * reaparece a cada carregamento até a regularização.
 *
 * CTA gera um PIX via API legada (POST /api/public/cobranca/pix) na conta Asaas
 * MASTER da SL — mesmos moldes do modal de migração. O valor é AUTORITATIVO no
 * servidor (dívida R$ 10.500,00 − 30% = R$ 7.350,00).
 */

// Só este cliente vê o modal de cobrança.
const CLIENTE_ALVO = 'webleiloes.com.br'
const PIX_ENDPOINT = '/api/public/cobranca/pix'

function hostname() {
  return (typeof window !== 'undefined' && window.location && window.location.hostname) || ''
}
function clienteBase(host) {
  return String(host || '').replace(/:\d+$/, '').replace(/^(erp|www)\./i, '').toLowerCase()
}

export default {
  name: 'ModalCobranca',
  data() {
    return {
      opened: false,
      preview: false,      // true só em teste local (?testemodalwebleiloes=1)
      payStatus: 'idle',   // idle → loading → done | paid | error
      pix: null,
      valor: 7350,
      payError: '',
      copied: false,
    }
  },
  computed: {
    qrDataUri() {
      const img = this.pix && this.pix.encodedImage
      if (!img) return null
      return img.startsWith('data:') ? img : `data:image/png;base64,${img}`
    },
    valorFmt() {
      return this.formatBRL(this.valor)
    },
  },
  mounted() {
    document.body.append(this.$el)
    // Preview APENAS local (localhost/127.0.0.1) via ?testemodalwebleiloes=1. Em
    // produção o param é ignorado — o modal segue restrito ao domínio webleiloes,
    // então ninguém dispara a tela de cobrança pra um cliente real por URL.
    const host = hostname()
    const isLocal = /^(localhost|127\.0\.0\.1|0\.0\.0\.0)$/.test(host)
    const search = (typeof window !== 'undefined' && window.location && window.location.search) || ''
    const preview = isLocal && /[?&]testemodalwebleiloes(=|&|$)/.test(search)
    if (clienteBase(host) !== CLIENTE_ALVO && !preview) return
    this.preview = preview
    this.$openTimer = setTimeout(() => { this.opened = true }, 1000)
  },
  beforeDestroy() {
    this.$openTimer && clearTimeout(this.$openTimer)
    document.body.contains(this.$el) && document.body.removeChild(this.$el)
  },
  methods: {
    formatBRL(v) {
      const n = Number(v) || 0
      return 'R$ ' + n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    fechar() {
      // Sem persistência: fecha só nesta visualização; reaparece no próximo load.
      this.opened = false
    },
    async regularizar() {
      if (this.payStatus === 'loading') return
      this.payStatus = 'loading'
      this.payError = ''
      try {
        // Anônima + uloc-mi: gerencia (mesmo motivo do modal de migração: evitar
        // 401 de sessão de cliente e deixar o backend ler a config da gerência).
        const base = (http && http.defaults && http.defaults.baseURL) || ''
        const bare = axios.create({ baseURL: base })
        // Em preview local, envia o domínio-alvo p/ o backend gerar o PIX real (sandbox);
        // em produção, o domínio de origem (webleiloes) é o próprio hostname.
        const dominio = this.preview ? CLIENTE_ALVO : hostname()
        const resp = await bare.post(PIX_ENDPOINT, { dominio }, { headers: { 'uloc-mi': 'gerencia' } })
        let data = resp && resp.data
        if (typeof data === 'string') {
          try { data = JSON.parse(data) } catch (e) { /* mantém string */ }
        }
        if (data && data.status === 'paid') {
          this.payStatus = 'paid'
          return
        }
        const pix = data && data.pix
        if (!pix || !pix.payload) {
          throw new Error((data && data.error) || 'Resposta sem dados de PIX.')
        }
        if (data.value) this.valor = data.value
        this.pix = pix
        this.payStatus = 'done'
      } catch (e) {
        let body = e && e.response && e.response.data
        if (typeof body === 'string') {
          try { body = JSON.parse(body) } catch (err) { /* mantém string */ }
        }
        const msg = (body && (body.message || body.error)) || (e && e.message) || 'Falha ao gerar o PIX. Tente novamente.'
        this.payError = (typeof msg === 'string' && msg) ? msg : 'Falha ao gerar o PIX. Tente novamente.'
        this.payStatus = 'error'
      }
    },
    async copiar() {
      const code = this.pix && this.pix.payload
      if (!code) return
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(code)
        } else {
          const ta = document.createElement('textarea')
          ta.value = code
          document.body.appendChild(ta)
          ta.select()
          document.execCommand('copy')
          document.body.removeChild(ta)
        }
        this.copied = true
        setTimeout(() => { this.copied = false }, 2200)
      } catch (e) { /* noop */ }
    },
    tentarNovamente() {
      this.payStatus = 'idle'
      this.payError = ''
    },
  },
}
</script>

<template>
  <div class="scob" v-if="opened">
    <div class="scob__overlay"></div>

    <div class="scob__card" role="dialog" aria-modal="true" aria-labelledby="scob-title">
      <button class="scob__x" type="button" aria-label="Fechar" @click="fechar">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
      </button>

      <div class="scob__inner">
        <!-- OFERTA / AVISO -->
        <template v-if="payStatus !== 'done' && payStatus !== 'paid'">
          <span class="scob__brand">Suporte Leilões</span>

          <div class="scob__block" role="alert">
            <span class="scob__block-ico">🔒</span>
            <span>Seu ERP será <strong>BLOQUEADO em 24 horas</strong> caso o pagamento não seja identificado.</span>
          </div>

          <h2 id="scob-title" class="scob__title">
            Precisamos da sua ajuda para <span class="scob__hl">continuar de pé</span> 🙏
          </h2>

          <p class="scob__text">
            Somos uma empresa que <strong>depende da mensalidade para sobreviver</strong> e seguir
            evoluindo. Estamos lançando um <strong>novo software</strong> para resolver muitos dos
            problemas de hoje e criar muitas novas soluções para você.
          </p>
          <p class="scob__text">
            Mas, antes de te oferecer a troca para essa nova geração, precisamos estar com as
            <strong>mensalidades em dia</strong>. Por isso, preparamos uma condição especial para
            você regularizar agora.
          </p>

          <div class="scob__price">
            <div class="scob__price-label">Quitação das mensalidades em atraso — só agora, com 30% de desconto</div>
            <div class="scob__price-row">
              <span class="scob__price-old">R$ 10.500,00</span>
              <span class="scob__price-value">R$ 7.350<span class="scob__price-cents">,00</span></span>
            </div>
            <div class="scob__price-note">pagamento via PIX · seu acesso segue normal assim que for identificado</div>
          </div>

          <button class="scob__cta" type="button" :disabled="payStatus === 'loading'" @click="regularizar">
            <template v-if="payStatus === 'loading'">
              <span class="scob__spin" aria-hidden="true"></span> Gerando seu PIX…
            </template>
            <template v-else>
              Quero regularizar agora com 30% de desconto
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
            </template>
          </button>

          <p v-if="payStatus === 'error'" class="scob__error">
            {{ payError }} <button type="button" class="scob__link" @click="tentarNovamente">Tentar novamente</button>
          </p>

          <p class="scob__auto">
            Esta é uma mensagem automática. A partir de agora, <strong>todos os nossos processos serão
            automatizados por IA</strong> — desde cobrança, suporte e monitoramento.
          </p>
        </template>

        <!-- PIX GERADO -->
        <template v-else-if="payStatus === 'done'">
          <span class="scob__brand">Suporte Leilões</span>
          <h2 class="scob__title scob__title--sm">Pague o PIX para regularizar e manter seu acesso</h2>
          <p class="scob__text">
            Escaneie o QR Code ou use o <strong>copia e cola</strong> abaixo. Assim que o pagamento de
            <strong>{{ valorFmt }}</strong> for identificado, seu ERP segue funcionando normalmente.
          </p>

          <div class="scob__pix">
            <img v-if="qrDataUri" :src="qrDataUri" alt="QR Code PIX" class="scob__qr" />
            <div class="scob__pix-copy">
              <label class="scob__pix-label">PIX copia e cola</label>
              <textarea class="scob__pix-code" readonly rows="4" :value="pix.payload" @focus="$event.target.select()"></textarea>
              <button class="scob__copy" type="button" @click="copiar">
                {{ copied ? '✓ Copiado!' : 'Copiar código PIX' }}
              </button>
            </div>
          </div>

          <p class="scob__auto">
            Esta é uma mensagem automática. A partir de agora, <strong>todos os nossos processos serão
            automatizados por IA</strong> — desde cobrança, suporte e monitoramento.
          </p>
        </template>

        <!-- JÁ PAGO -->
        <template v-else>
          <span class="scob__brand">Suporte Leilões</span>
          <h2 class="scob__title scob__title--sm">Pagamento identificado — obrigado! 🎉</h2>
          <p class="scob__text">
            Suas mensalidades foram <strong>regularizadas</strong> e seu acesso está garantido. Em breve
            falamos com você sobre a migração para o nosso novo ERP. Obrigado por seguir com a gente!
          </p>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped>
.scob {
  position: fixed;
  inset: 0;
  z-index: 12060;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
}
.scob__overlay {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(ellipse 70% 50% at 50% 0%, rgba(220, 38, 38, .28), transparent 60%),
    radial-gradient(ellipse 60% 50% at 85% 20%, rgba(245, 158, 11, .12), transparent 60%),
    #050816;
}
.scob__card {
  position: relative;
  width: 100%;
  height: 100%;
  overflow-y: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: scob-fade .3s ease;
}
.scob__x {
  position: absolute;
  top: 18px; right: 18px;
  width: 38px; height: 38px;
  display: inline-flex; align-items: center; justify-content: center;
  border: none; border-radius: 50%;
  background: rgba(255, 255, 255, .07);
  color: rgba(255, 255, 255, .6);
  cursor: pointer;
  transition: background .15s, color .15s;
  z-index: 2;
}
.scob__x:hover { background: rgba(255, 255, 255, .14); color: #fff; }

.scob__inner {
  width: 100%;
  max-width: 640px;
  padding: 40px clamp(22px, 5vw, 40px);
  text-align: left;
}

.scob__brand {
  display: inline-block;
  font-size: 13px; font-weight: 800; letter-spacing: .14em; text-transform: uppercase;
  color: #8AB4FB;
  margin-bottom: 20px;
}

.scob__block {
  display: flex; align-items: center; gap: 12px;
  padding: 14px 16px;
  margin-bottom: 22px;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(220, 38, 38, .26), rgba(220, 38, 38, .12));
  border: 1px solid rgba(248, 113, 113, .5);
  font-size: 15px;
  color: #fde2e2;
  animation: scob-pulse 1.6s infinite;
}
.scob__block-ico { flex: none; font-size: 22px; }
.scob__block strong { color: #fff; font-weight: 800; }

.scob__title {
  font-size: clamp(26px, 4vw, 34px);
  line-height: 1.16;
  font-weight: 800;
  color: #fff;
  margin: 0 0 18px;
  letter-spacing: -.01em;
}
.scob__title--sm { font-size: clamp(21px, 3vw, 26px); }
.scob__hl {
  background: linear-gradient(135deg, #5C8DF6, #8AB4FB);
  -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;
}
.scob__text {
  font-size: 15.5px;
  line-height: 1.65;
  color: rgba(226, 232, 240, .85);
  margin: 0 0 15px;
}
.scob__text strong { color: #fff; font-weight: 600; }

.scob__price {
  border: 1px solid rgba(245, 158, 11, .3);
  border-radius: 16px;
  background:
    radial-gradient(ellipse 70% 90% at 50% 0%, rgba(245, 158, 11, .12), transparent 70%),
    rgba(255, 255, 255, .02);
  padding: 18px 18px;
  margin: 22px 0;
  text-align: center;
}
.scob__price-label {
  font-size: 13px; font-weight: 600; color: rgba(203, 213, 225, .88);
  margin-bottom: 8px;
}
.scob__price-row {
  display: flex; align-items: baseline; justify-content: center; gap: 14px;
}
.scob__price-old {
  font-size: 20px; color: rgba(148, 163, 184, .8);
  text-decoration: line-through;
}
.scob__price-value {
  font-size: 44px; font-weight: 800; line-height: 1; color: #fff; letter-spacing: -.02em;
}
.scob__price-cents { font-size: 22px; font-weight: 700; color: rgba(255,255,255,.75); }
.scob__price-note {
  font-size: 12.5px; color: rgba(148, 163, 184, .85); margin-top: 10px;
}

.scob__cta {
  width: 100%;
  display: inline-flex; align-items: center; justify-content: center; gap: 9px;
  min-height: 58px;
  padding: 8px 18px;
  border: none; border-radius: 13px;
  background: linear-gradient(135deg, #2563EB, #1D4FD8);
  color: #fff;
  font-family: inherit; font-size: 16px; font-weight: 700;
  cursor: pointer; text-align: center;
  box-shadow: 0 14px 34px -10px rgba(37, 99, 235, .85), 0 0 0 1px rgba(124, 168, 247, .2) inset;
  transition: transform .12s ease, filter .2s ease;
}
.scob__cta:hover:not(:disabled) { transform: translateY(-1px); filter: brightness(1.06); }
.scob__cta:active { transform: translateY(0); }
.scob__cta:disabled { opacity: .8; cursor: default; }
.scob__cta svg { transition: transform .15s ease; }
.scob__cta:hover:not(:disabled) svg { transform: translateX(3px); }

.scob__spin {
  width: 17px; height: 17px;
  border: 2.5px solid rgba(255,255,255,.35); border-top-color: #fff;
  border-radius: 50%; display: inline-block; animation: scob-spin .7s linear infinite;
}
@keyframes scob-spin { to { transform: rotate(360deg); } }

.scob__error { margin: 12px 0 0; font-size: 13.5px; color: #fca5a5; text-align: center; }
.scob__link {
  background: none; border: none; padding: 0; color: #8AB4FB; font: inherit; font-weight: 700;
  cursor: pointer; text-decoration: underline;
}

.scob__pix {
  display: flex; gap: 20px; align-items: center; flex-wrap: wrap;
  margin: 10px 0 8px;
}
.scob__qr {
  flex: none; width: 190px; height: 190px;
  border-radius: 14px; background: #fff; padding: 9px;
  box-shadow: 0 10px 30px -10px rgba(0,0,0,.6);
}
.scob__pix-copy { flex: 1 1 230px; display: flex; flex-direction: column; gap: 8px; }
.scob__pix-label {
  font-size: 12px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase;
  color: rgba(148, 163, 184, .9);
}
.scob__pix-code {
  width: 100%; resize: none; border-radius: 10px;
  border: 1px solid rgba(138, 180, 251, .22);
  background: rgba(5, 8, 22, .5); color: #e2e8f0;
  font-family: 'SFMono-Regular', ui-monospace, Menlo, Consolas, monospace;
  font-size: 12px; line-height: 1.5; padding: 10px 12px; word-break: break-all;
}
.scob__copy {
  width: 100%; height: 46px; border: none; border-radius: 11px;
  background: linear-gradient(135deg, #2563EB, #1D4FD8); color: #fff;
  font-family: inherit; font-size: 15px; font-weight: 700; cursor: pointer;
  transition: filter .15s;
}
.scob__copy:hover { filter: brightness(1.08); }

.scob__auto {
  margin: 26px 0 0;
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px dashed rgba(138, 180, 251, .28);
  background: rgba(37, 99, 235, .06);
  font-size: 12.8px; line-height: 1.55;
  color: rgba(148, 163, 184, .92);
}
.scob__auto strong { color: #cfe0ff; }

@keyframes scob-fade { from { opacity: 0 } to { opacity: 1 } }
@keyframes scob-pulse {
  0%, 100% { border-color: rgba(248, 113, 113, .5); }
  50% { border-color: rgba(248, 113, 113, .9); }
}

@media (max-width: 560px) {
  .scob__inner { padding: 32px 20px; }
  .scob__pix { justify-content: center; }
  .scob__qr { width: 210px; height: 210px; }
}
</style>
